import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import type { TooltipProps } from 'recharts';
import type { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';

import { ChartConfig, ChartContainer, ChartTooltip } from '@/components/ui/chart';
import { Skeleton } from '@/components/ui/skeleton';
import { AnalyticsEmptyState } from '@/modules/analytics/components/aiAgents/AnalyticsEmptyState';
import {
  MetricTooltipCard,
  MetricTooltipRow,
} from '@/modules/analytics/components/tooltip/MetricTooltipCard';
import { CampaignDailyPtpPoint } from '@/shared/types/campaigns';
import { formatDate } from '@/util/dateUtils';

import { formatMetricTick, formatMetricValue } from './formatMetric';
import { CumulativePoint, toCumulativePtp } from './ptpSeries';

interface ChartCardProps {
  title: string;
  /** Unit the chart is plotted in, shown under the title. */
  unit: string;
  series: CumulativePoint[];
  hasData: boolean;
  emptyDescription: string;
  /** Exact value, for the tooltip. */
  formatValue: (value: number) => string;
  /** Short form for the axis, whose gutter is fixed and clips anything wider. */
  formatTick: (value: number) => string;
}

// Only the colour is consumed, via `--color-value`: no legend, and the tooltip is ours.
const chartConfig = { value: { color: 'var(--chart-1)' } } satisfies ChartConfig;

const CumulativeTooltip = ({
  active,
  payload,
  label,
  title,
  formatValue,
}: TooltipProps<ValueType, NameType> & {
  title: string;
  formatValue: (value: number) => string;
}) => {
  if (!active || !payload?.length) {
    return null;
  }
  return (
    <MetricTooltipCard title={formatDate(label as string, 'MMM d, yyyy')}>
      <MetricTooltipRow label={title} value={formatValue(payload[0].value as number)} />
    </MetricTooltipCard>
  );
};

const ChartCard = ({
  title,
  unit,
  series,
  hasData,
  emptyDescription,
  formatValue,
  formatTick,
}: ChartCardProps) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col rounded-xl border border-stroke bg-background p-6">
      <h3 className="text-sm leading-5 font-semibold text-foreground">{title}</h3>
      <p className="mb-4 text-xs leading-4 text-foreground-muted">
        {t('campaigns.performance.units.chart', { 0: unit })}
      </p>
      {hasData ? (
        <ChartContainer className="aspect-3/2 w-full" config={chartConfig}>
          <AreaChart data={series} margin={{ top: 4, right: 8, bottom: 0, left: 4 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              axisLine={false}
              dataKey="date"
              interval="preserveStartEnd"
              minTickGap={32}
              tickFormatter={(value) => formatDate(value, 'MMM d')}
              tickLine={false}
              tickMargin={8}
            />
            <YAxis
              axisLine={false}
              tickCount={5}
              tickFormatter={formatTick}
              tickLine={false}
              tickMargin={8}
              width={60}
            />
            <ChartTooltip content={<CumulativeTooltip formatValue={formatValue} title={title} />} />
            <Area
              dataKey="value"
              fill="var(--color-value)"
              fillOpacity={0.25}
              stroke="var(--color-value)"
              strokeWidth={2}
              type="monotone"
            />
          </AreaChart>
        </ChartContainer>
      ) : (
        <div className="flex aspect-3/2 w-full items-center justify-center">
          <AnalyticsEmptyState
            description={emptyDescription}
            title={t('analytics.no_data_available')}
          />
        </div>
      )}
    </div>
  );
};

interface Props {
  dailyPtp?: CampaignDailyPtpPoint[];
  currency?: string;
  isLoading?: boolean;
}

/**
 * The two promise-to-pay charts, both cumulative. The amount chart is often empty
 * while the count one is not: many campaigns have promises with no amount captured.
 */
export const PromisesOverTime = ({ dailyPtp, currency, isLoading }: Props) => {
  const { t } = useTranslation();

  const counts = useMemo(() => toCumulativePtp(dailyPtp, 'ptp_count'), [dailyPtp]);
  const amounts = useMemo(() => toCumulativePtp(dailyPtp, 'ptp_amount'), [dailyPtp]);

  if (isLoading) {
    return (
      <section>
        <h2 className="mb-4 text-base leading-6 font-semibold text-foreground">
          {t('campaigns.performance.sections.promises')}
        </h2>
        <div className="grid gap-6 lg:grid-cols-2">
          <Skeleton className="h-72 w-full rounded-xl" />
          <Skeleton className="h-72 w-full rounded-xl" />
        </div>
      </section>
    );
  }

  return (
    <section>
      <h2 className="mb-4 text-base leading-6 font-semibold text-foreground">
        {t('campaigns.performance.sections.promises')}
      </h2>
      <div className="grid gap-6 lg:grid-cols-2">
        <ChartCard
          emptyDescription={t('campaigns.performance.charts.no_promises')}
          formatTick={(value) => formatMetricTick(value, 'count')}
          formatValue={(value) => formatMetricValue(value, 'count')}
          hasData={counts.hasData}
          series={counts.series}
          title={t('campaigns.performance.charts.ptp_count')}
          unit={t('campaigns.performance.units.debts')}
        />
        <ChartCard
          emptyDescription={t('campaigns.performance.charts.no_amounts')}
          formatTick={(value) => formatMetricTick(value, 'currency', currency)}
          formatValue={(value) => formatMetricValue(value, 'currency', currency)}
          hasData={amounts.hasData}
          series={amounts.series}
          title={t('campaigns.performance.charts.ptp_amount')}
          unit={currency ?? t('campaigns.performance.units.currency')}
        />
      </div>
    </section>
  );
};
